import { CircularProgress, Typography } from "@material-ui/core"
import { useMemo } from "react"
import styled from "styled-components"

const StyledTextAnimation = styled(Typography).attrs<{
  $opacity: number
  $translateY: number
  component: any
}>(({ $opacity, $translateY }) => ({
  style: {
    opacity: $opacity,
    transform: `translateY(${100 - ($translateY * 100) / 2}px)`,
    visibility: $opacity === 0 ? "hidden" : "visible",
  },
}))<{ $opacity: number; $translateY: number }>`
  text-transform: uppercase;
  color: white;
  pointer-events: all;
`
const StyledH2 = styled(StyledTextAnimation)<{ component: any }>`
  ${(p) => p.theme.breakpoints.up("md")} {
    font-size: 32px;
  }
`

const StyledH1 = styled(StyledTextAnimation)`
  ${(p) => p.theme.breakpoints.up("md")} {
    font-weight: 300;
    font-size: 40px;
  }
`

const StyledSubtitle = styled(StyledTextAnimation)<{
  $hide?: boolean
  component: any
}>`
  text-transform: uppercase;
  color: white;
  visibility: ${(p) => (p.$hide ? "hidden" : "visible")};
`

const StyledH1Heb = styled(StyledTextAnimation)<{ component: any }>`
  ${(p) => p.theme.breakpoints.up("md")} {
    font-size: 40px;
  }
`

const StyledBody = styled(StyledTextAnimation)`
  text-transform: uppercase;
  color: white;
`
const StyledProgressCircle = styled(StyledTextAnimation).attrs({
  component: "div",
})`
  position: absolute;
  inset: 0;
  inset-block-start: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: rgb(153, 153, 153);
`
const StyledProgressCircleWrapper = styled.div<{ $show: boolean }>`
  /* position: relative; */
  /* transform: translateY(calc(-200% - 2px)); */
  visibility: ${(p) => (p.$show ? "visible" : "hidden")};
  transform: translateY(40px);
  pointer-events: none;
  ${(p) => p.theme.breakpoints.up("md")} {
    transform: translateY(57px);
  }
`

export enum FloatingTextVariant {
  HEADER1,
  HEADER2,
  SUBTITLE,
  BODY,
  REGULAR,
  HEADER1HEB,
  ARROW_DOWN,
}

const TRANSITION_FRAME_DURATION = 15

type Props = {
  count: number
  start: number
  end: number
  middle?: number
  variant: FloatingTextVariant
  text: string | JSX.Element
  alwaysShow?: boolean
  progress?: number
}

function FloatingText({
  count,
  middle,
  start,
  end,
  variant,
  text,
  alwaysShow = false,
  progress,
}: Props) {
  const opacity = useMemo(() => {
    const startingToFadeOut = end - TRANSITION_FRAME_DURATION
    const endOfFadeIn = middle ? middle : start + TRANSITION_FRAME_DURATION
    // tamplate: // ((count - start) / (end - start)) * 1

    if (count === 0 && start === 0) {
      return 1
    } else if (start !== 0 && count >= start && count <= endOfFadeIn) {
      // fade-in - linear opacity:
      return ((count - start) / (endOfFadeIn - start)) * 1
    } else if (count >= start && count <= startingToFadeOut) {
      // regular opacity:
      return 1
    } else if (count >= start && count <= end) {
      // fade-out opacity:
      return 1 - ((count - startingToFadeOut) / (end - startingToFadeOut)) * 1
    } else {
      return 0
    }
  }, [count, start, end, middle, alwaysShow])

  const translateY = useMemo(() => {
    const startingToFadeOut = end - TRANSITION_FRAME_DURATION
    const endOfFadeIn = middle ? middle : start + TRANSITION_FRAME_DURATION
    // tamplate: // ((count - start) / (end - start)) * 1
    if (count >= start && count <= endOfFadeIn) {
      // fade-in - linear opacity:
      return ((count - start) / (endOfFadeIn - start)) * 1
    } else if (count >= start && count <= startingToFadeOut) {
      // regular Y:
      return 1
    } else {
      return 1
    }
  }, [count, start, end, middle])

  return variant === FloatingTextVariant.HEADER1 ? (
    <StyledH1 variant="h1" $opacity={opacity} $translateY={translateY}>
      {text}
    </StyledH1>
  ) : variant === FloatingTextVariant.SUBTITLE ? (
    <StyledSubtitle
      variant="h3"
      component="p"
      $opacity={opacity}
      $translateY={translateY}
    >
      {text}
    </StyledSubtitle>
  ) : variant === FloatingTextVariant.BODY ? (
    <StyledBody variant="body1" $opacity={opacity} $translateY={translateY}>
      {text}
    </StyledBody>
  ) : variant === FloatingTextVariant.HEADER1HEB ? (
    <StyledH1Heb
      variant="h1"
      component="p"
      $opacity={opacity}
      $translateY={translateY}
    >
      {text}
    </StyledH1Heb>
  ) : variant === FloatingTextVariant.ARROW_DOWN ? (
    <>
      <StyledProgressCircle $opacity={opacity} $translateY={translateY}>
        <StyledProgressCircleWrapper $show={progress < 100}>
          <CircularProgress
            color="inherit"
            thickness={1.5}
            size={50}
            variant="determinate"
            value={progress}
          />
        </StyledProgressCircleWrapper>
      </StyledProgressCircle>
      <StyledSubtitle
        variant="h3"
        component="div"
        $opacity={opacity}
        $translateY={translateY}
        $hide={progress < 100}
      >
        {text}
      </StyledSubtitle>
    </>
  ) : (
    <StyledH2
      variant="h2"
      component="p"
      $opacity={opacity}
      $translateY={translateY}
    >
      {text}
    </StyledH2>
  )
}

export default FloatingText
